/**
 * Deal Pipeline Detail Page - timeline, history, metrics, outcome and feedback editor.
 */
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  ArrowRight,
  Loader2,
  Clock,
  Calendar,
  CheckCircle2,
  Save,
  DollarSign,
  Target,
  FolderLock,
  Layers3,
} from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import dealPipelineService from '../services/dealPipelineService';
import dealRoomService from '../services/dealRoomService';
import { DealRoom } from '../types/dealRoom';
import {
  DealPipeline,
  PipelineStage,
  RecordOutcomeRequest,
  UpdatePipelineRequest,
} from '../types/dealPipeline';
import PipelineStageBadge, {
  PIPELINE_STAGES,
  STAGE_CONFIG,
} from '../components/PipelineStageBadge';
import AdvanceStageModal from '../components/AdvanceStageModal';
import RecordOutcomeModal from '../components/RecordOutcomeModal';

const formatDate = (s?: string | null): string =>
  s ? new Date(s).toLocaleDateString() : '—';
const formatDateTime = (s?: string | null): string =>
  s ? new Date(s).toLocaleString() : '—';

const formatCurrency = (n?: number | null): string => {
  if (n == null) return '—';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const outcomeBadgeColor = (o?: string | null): string => {
  switch (o) {
    case 'funded':
      return 'bg-green-100 text-green-700';
    case 'passed':
      return 'bg-red-100 text-red-700';
    case 'stalled':
      return 'bg-amber-100 text-amber-700';
    case 'withdrawn':
      return 'bg-gray-100 text-gray-700';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const DealPipelineDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState<DealPipeline | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [advanceOpen, setAdvanceOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);

  // Deal room linkage
  const [linkedDealRoom, setLinkedDealRoom] = useState<DealRoom | null>(null);
  const [dealRoomPromptOpen, setDealRoomPromptOpen] = useState(false);

  // Editable feedback fields
  const [investorFeedback, setInvestorFeedback] = useState('');
  const [founderFeedback, setFounderFeedback] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [savingFeedback, setSavingFeedback] = useState(false);

  const load = async () => {
    const pid = Number(id);
    if (!pid) {
      toast.error('Invalid pipeline id');
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    const result = await dealPipelineService.fetchPipeline(pid);
    if (result.error) {
      toast.error(result.error);
    } else if (result.data) {
      setPipeline(result.data);
      setInvestorFeedback(result.data.investor_feedback || '');
      setFounderFeedback(result.data.founder_feedback || '');
      setAdminNotes(result.data.admin_notes || '');
    }
    setIsLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Check for linked deal room whenever the pipeline loads
  useEffect(() => {
    const pid = Number(id);
    if (!pid) return;
    const lookup = async () => {
      const res = await dealRoomService.fetchDealRooms({ pipeline_id: pid });
      if (res.data && res.data.deal_rooms.length > 0) {
        setLinkedDealRoom(res.data.deal_rooms[0]);
      } else {
        setLinkedDealRoom(null);
      }
    };
    lookup();
  }, [id]);

  const handleDealRoomClick = () => {
    if (linkedDealRoom) {
      navigate(`/deal-rooms/${linkedDealRoom.id}`);
    } else {
      setDealRoomPromptOpen(true);
    }
  };

  const handleAdvance = async (newStage: PipelineStage, notes?: string) => {
    if (!pipeline) return;
    const result = await dealPipelineService.advanceStage(pipeline.id, newStage, notes);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data) setPipeline(result.data);
    toast.success(`Advanced to ${STAGE_CONFIG[newStage].label}`);
    setAdvanceOpen(false);
  };

  const handleRecordOutcome = async (payload: RecordOutcomeRequest) => {
    if (!pipeline) return;
    const result = await dealPipelineService.recordOutcome(pipeline.id, payload);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data) setPipeline(result.data);
    toast.success('Outcome recorded');
    setOutcomeOpen(false);
  };

  const handleSaveFeedback = async () => {
    if (!pipeline) return;
    setSavingFeedback(true);
    const payload: UpdatePipelineRequest = {
      investor_feedback: investorFeedback || null,
      founder_feedback: founderFeedback || null,
      admin_notes: adminNotes || null,
    };
    const result = await dealPipelineService.updatePipeline(pipeline.id, payload);
    setSavingFeedback(false);
    if (result.error) {
      toast.error(result.error);
      return;
    }
    if (result.data) setPipeline(result.data);
    toast.success('Feedback saved');
  };

  if (isLoading) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 flex items-center justify-center text-gray-600">
          <Loader2 className="w-5 h-5 mr-3 animate-spin" />
          Loading pipeline...
        </div>
      </DashboardLayout>
    );
  }

  if (!pipeline) {
    return (
      <DashboardLayout>
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center text-gray-600">
          <p className="mb-4">Pipeline not found.</p>
          <Link to="/deal-pipeline" className="text-blue-600 hover:underline">
            Back to pipeline
          </Link>
        </div>
      </DashboardLayout>
    );
  }

  const investorName = pipeline.investor?.full_name || `Investor #${pipeline.investor_id}`;
  const targetName =
    pipeline.project?.project_name ||
    pipeline.startup?.name ||
    (pipeline.project_id
      ? `Project #${pipeline.project_id}`
      : pipeline.startup_id
      ? `Startup #${pipeline.startup_id}`
      : '—');

  const currentIdx = PIPELINE_STAGES.indexOf(pipeline.stage);
  const stageHistory = pipeline.stage_history || [];

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Top bar */}
        <div className="flex items-center justify-between">
          <Link
            to="/deal-pipeline"
            className="inline-flex items-center text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Back to pipeline
          </Link>
          <div className="flex gap-2">
            <button
              onClick={handleDealRoomClick}
              className="inline-flex items-center px-3 py-1.5 border border-purple-300 text-purple-700 rounded-lg text-sm font-medium hover:bg-purple-50"
            >
              <FolderLock className="w-4 h-4 mr-1.5" />
              {linkedDealRoom ? 'Open Deal Room' : 'Create Deal Room'}
            </button>
            {pipeline &&
              (['soft_commitment', 'closed'] as PipelineStage[]).includes(
                pipeline.stage
              ) && (
                <Link
                  to={`/syndication/new?pipeline_id=${pipeline.id}`}
                  className="inline-flex items-center px-3 py-1.5 border border-indigo-300 text-indigo-700 rounded-lg text-sm font-medium hover:bg-indigo-50"
                >
                  <Layers3 className="w-4 h-4 mr-1.5" />
                  Create Syndication Round
                </Link>
              )}
            <button
              onClick={() => setAdvanceOpen(true)}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-50"
            >
              <ArrowRight className="w-4 h-4 mr-1.5" />
              Advance Stage
            </button>
            <button
              onClick={() => setOutcomeOpen(true)}
              className="inline-flex items-center px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-sm font-medium hover:bg-green-50"
            >
              <CheckCircle2 className="w-4 h-4 mr-1.5" />
              Record Outcome
            </button>
          </div>
        </div>

        {/* Header card */}
        <div className="bg-white border border-gray-200 rounded-xl p-6">
          <div className="flex items-center gap-6">
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-gray-500">Investor</div>
              <Link
                to={`/investors/${pipeline.investor_id}`}
                className="text-xl font-semibold text-gray-900 hover:text-blue-600 truncate block"
              >
                {investorName}
              </Link>
              {pipeline.investor?.firm_name && (
                <div className="text-sm text-gray-600 truncate">
                  {pipeline.investor.firm_name}
                </div>
              )}
            </div>
            <ArrowRight className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-xs uppercase tracking-wide text-gray-500">
                {pipeline.project_id ? 'Project' : 'Startup'}
              </div>
              <div className="text-xl font-semibold text-gray-900 truncate">
                {targetName}
              </div>
            </div>
            <div className="flex-shrink-0 text-right">
              <PipelineStageBadge stage={pipeline.stage} />
              {pipeline.days_in_pipeline != null && (
                <div className="text-xs text-gray-500 mt-2 inline-flex items-center">
                  <Clock className="w-3 h-3 mr-1" />
                  {pipeline.days_in_pipeline} days in pipeline
                </div>
              )}
            </div>
          </div>

          {pipeline.outcome && (
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <span
                className={`px-2.5 py-1 text-xs font-medium rounded-full ${outcomeBadgeColor(
                  pipeline.outcome
                )}`}
              >
                Outcome: {pipeline.outcome}
              </span>
              {pipeline.outcome_date && (
                <span className="text-xs text-gray-500">
                  {formatDate(pipeline.outcome_date)}
                </span>
              )}
            </div>
          )}
        </div>

        {/* Stage timeline (horizontal stepper) */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Stage timeline</h3>
          <div className="overflow-x-auto pb-2">
            <div className="flex items-center" style={{ minWidth: `${PIPELINE_STAGES.length * 100}px` }}>
              {PIPELINE_STAGES.map((stage, idx) => {
                const cfg = STAGE_CONFIG[stage];
                const isPast = idx < currentIdx;
                const isCurrent = idx === currentIdx;
                const histEntry = stageHistory.find((h) => h.stage === stage);
                return (
                  <React.Fragment key={stage}>
                    <div className="flex flex-col items-center text-center w-[100px]">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold border-2 ${
                          isCurrent
                            ? `${cfg.dot} text-white border-transparent`
                            : isPast
                            ? 'bg-green-500 text-white border-transparent'
                            : 'bg-white text-gray-400 border-gray-300'
                        }`}
                        title={cfg.label}
                      >
                        {isPast ? '✓' : idx + 1}
                      </div>
                      <div className={`text-xs mt-1 ${isCurrent ? `${cfg.text} font-medium` : 'text-gray-500'}`}>
                        {cfg.label}
                      </div>
                      {histEntry?.entered_at && (
                        <div className="text-[10px] text-gray-400 mt-0.5">
                          {formatDate(histEntry.entered_at)}
                        </div>
                      )}
                    </div>
                    {idx < PIPELINE_STAGES.length - 1 && (
                      <div className={`flex-1 h-0.5 ${isPast ? 'bg-green-400' : 'bg-gray-200'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* Engagement metrics */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Engagement metrics</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Metric
              icon={<Calendar className="w-4 h-4 text-blue-500" />}
              label="Intro date"
              value={formatDate(pipeline.intro_date)}
            />
            <Metric
              icon={<Target className="w-4 h-4 text-purple-500" />}
              label="Intro method"
              value={pipeline.intro_method || '—'}
            />
            <Metric
              icon={<Clock className="w-4 h-4 text-indigo-500" />}
              label="Meetings"
              value={String(pipeline.meeting_count ?? 0)}
            />
            <Metric
              icon={<Calendar className="w-4 h-4 text-amber-500" />}
              label="Last activity"
              value={formatDate(pipeline.last_activity_date)}
            />
          </div>
        </section>

        {/* Outcome panel */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Outcome</h3>
          {pipeline.outcome ? (
            <div className="space-y-2 text-sm">
              <div>
                <span className="text-gray-500">Outcome:</span>{' '}
                <span
                  className={`px-2 py-0.5 rounded text-xs font-medium ${outcomeBadgeColor(
                    pipeline.outcome
                  )}`}
                >
                  {pipeline.outcome}
                </span>
              </div>
              {pipeline.outcome_date && (
                <div>
                  <span className="text-gray-500">Date:</span> {formatDate(pipeline.outcome_date)}
                </div>
              )}
              {pipeline.pass_reason && (
                <div>
                  <span className="text-gray-500">Pass reason:</span>{' '}
                  <span className="capitalize">{String(pipeline.pass_reason).replace(/_/g, ' ')}</span>
                </div>
              )}
              {pipeline.pass_notes && (
                <div>
                  <span className="text-gray-500">Notes:</span>{' '}
                  <span className="whitespace-pre-wrap">{pipeline.pass_notes}</span>
                </div>
              )}
              {pipeline.funded_amount != null && (
                <div>
                  <DollarSign className="inline w-4 h-4 text-green-600 mr-1" />
                  <span className="font-medium text-green-700">
                    {formatCurrency(pipeline.funded_amount)}
                  </span>
                  {pipeline.funded_instrument && (
                    <span className="text-gray-600"> — {pipeline.funded_instrument}</span>
                  )}
                </div>
              )}
              {pipeline.funded_terms && (
                <div>
                  <span className="text-gray-500">Terms:</span>{' '}
                  <span className="whitespace-pre-wrap">{pipeline.funded_terms}</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <p className="text-sm text-gray-500 mb-3">No outcome recorded yet.</p>
              <button
                onClick={() => setOutcomeOpen(true)}
                className="inline-flex items-center px-3 py-1.5 border border-green-300 text-green-700 rounded-lg text-sm font-medium hover:bg-green-50"
              >
                <CheckCircle2 className="w-4 h-4 mr-1.5" />
                Record Outcome
              </button>
            </div>
          )}
        </section>

        {/* Feedback editors */}
        <section className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900">Feedback &amp; notes</h3>
            <button
              onClick={handleSaveFeedback}
              disabled={savingFeedback}
              className="inline-flex items-center px-3 py-1.5 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {savingFeedback ? (
                <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-1.5" />
              )}
              Save
            </button>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Investor feedback
            </label>
            <textarea
              value={investorFeedback}
              onChange={(e) => setInvestorFeedback(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="What did the investor say?"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Founder feedback
            </label>
            <textarea
              value={founderFeedback}
              onChange={(e) => setFounderFeedback(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Founder's perspective on this investor"
            />
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
              Admin notes
            </label>
            <textarea
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
              placeholder="Internal notes"
            />
          </div>
        </section>

        {/* Activity / stage history log */}
        <section className="bg-white border border-gray-200 rounded-xl p-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Activity log</h3>
          {stageHistory.length === 0 ? (
            <div className="text-center py-6 text-gray-400 text-sm">
              <Clock className="w-6 h-6 mx-auto mb-2 opacity-50" />
              No stage transitions yet
            </div>
          ) : (
            <div className="space-y-3">
              {stageHistory.map((entry, idx) => {
                const cfg = STAGE_CONFIG[entry.stage];
                return (
                  <div
                    key={`${entry.stage}-${idx}`}
                    className="flex items-start gap-3 border-l-2 border-gray-200 pl-4 py-2"
                  >
                    <div
                      className={`w-3 h-3 rounded-full mt-1.5 flex-shrink-0 -ml-[1.4rem] ${cfg?.dot || 'bg-gray-400'}`}
                    />
                    <div className="flex-1">
                      <p className="text-sm text-gray-900">
                        Entered{' '}
                        <span className="font-medium">{cfg?.label || entry.stage}</span>
                      </p>
                      {entry.notes && (
                        <p className="text-xs text-gray-600 mt-0.5 whitespace-pre-wrap">
                          {entry.notes}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">
                        {formatDateTime(entry.entered_at)}
                        {entry.exited_at && ` — exited ${formatDateTime(entry.exited_at)}`}
                        {entry.actor_id && ` by ${entry.actor_id}`}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </div>

      <AdvanceStageModal
        open={advanceOpen}
        currentStage={pipeline.stage}
        onClose={() => setAdvanceOpen(false)}
        onConfirm={handleAdvance}
      />
      <RecordOutcomeModal
        open={outcomeOpen}
        onClose={() => setOutcomeOpen(false)}
        onConfirm={handleRecordOutcome}
      />

      {dealRoomPromptOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDealRoomPromptOpen(false)}
        >
          <div
            className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 space-y-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <FolderLock className="w-6 h-6 text-purple-600" />
              <h2 className="text-lg font-semibold text-gray-900">
                Create Deal Room
              </h2>
            </div>
            <p className="text-sm text-gray-600">
              No deal room exists for this pipeline yet. Create one to share
              documents, manage NDAs, and track investor engagement.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDealRoomPromptOpen(false)}
                className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  navigate(`/deal-rooms/new?pipeline_id=${pipeline.id}`)
                }
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700"
              >
                <FolderLock className="w-4 h-4 mr-2" />
                Create deal room
              </button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

const Metric: React.FC<{
  icon: React.ReactNode;
  label: string;
  value: string;
}> = ({ icon, label, value }) => (
  <div className="border border-gray-200 rounded-lg p-3">
    <div className="flex items-center gap-2 text-xs text-gray-500">
      {icon}
      {label}
    </div>
    <div className="text-sm font-medium text-gray-900 mt-1">{value}</div>
  </div>
);

export default DealPipelineDetailPage;
